import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Users, ShoppingBag, Camera, Wrench, Mail, Menu, X, Shield } from 'lucide-react';
import PrivacyPolicyPage from './PrivacyPolicy';
import Gallery from './components/Gallery';

const DixielandCruisersApp = () => {
  const [activeTab, setActiveTab] = useState('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handleTabChange = (tabId) => {
    console.log('Tab change requested:', tabId); // Debug log
    setActiveTab(tabId);
    setIsMenuOpen(false);
    
    if (tabId === 'home') {
      window.location.hash = '';
    } else {
      window.location.hash = tabId;
    }
  };

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      const path = window.location.pathname.replace('/', '');
      
      // Check hash first, then path (for Discord redirect support)
      const activeRoute = hash || path;
      
      if (activeRoute && ['events', 'gallery', 'store', 'tech', 'contact', 'privacy'].includes(activeRoute)) {
        setActiveTab(activeRoute);
        // If we came from a path route, update the hash for consistency
        if (!hash && path) {
          window.location.hash = path;
        }
      } else {
        setActiveTab('home');
      }
    };

    handleHashChange();

    window.addEventListener('hashchange', handleHashChange);
    window.addEventListener('popstate', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('popstate', handleHashChange);
    };
  }, []);

  const backgroundImages = [
    '/images/land-cruiser-1.jpg',
    '/images/land-cruiser-2.jpg',
    '/images/land-cruiser-3.jpg',
    '/images/land-cruiser-4.jpg',
    '/images/land-cruiser-5.jpg',
    '/images/land-cruiser-6.jpg'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % backgroundImages.length);
    }, 10000);
    
    return () => clearInterval(interval);
  }, [backgroundImages.length]);

  const events = [
    {
      id: 1,
      title: 'Monthly Meet & Greet',
      date: '2025-08-15',
      time: '6:00 PM',
      location: 'Billy\'s Bar & Grill - Birmingham',
      description: 'Monthly gathering to plan upcoming trails and share stories.',
      attendees: 24,
      image: '/images/land-cruiser-2.jpg'
    },
    {
      id: 2,
      title: 'Cheaha State Park Trail Run',
      date: '2025-08-22',
      time: '9:00 AM',
      location: 'Cheaha State Park',
      description: 'Moderate difficulty trail with scenic mountain views.',
      attendees: 16,
      image: '/images/land-cruiser-4.jpg'
    }
  ];

  const formatDate = (dateString) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const NavMenu = () => (
    <nav className={`nav ${isMenuOpen ? 'mobile-open' : ''}`}>
      {[
        { id: 'home', label: 'Home', icon: Users },
        { id: 'events', label: 'Events', icon: Calendar },
        { id: 'gallery', label: 'Gallery', icon: Camera },
        { id: 'store', label: 'Store', icon: ShoppingBag },
        { id: 'tech', label: 'Tech Tips', icon: Wrench },
        { id: 'contact', label: 'Contact', icon: Mail },
        { id: 'privacy', label: 'Privacy', icon: Shield }
      ].map(({ id, label, icon: Icon }) => (
        <button
          key={id}
          onClick={() => handleTabChange(id)}
          className={`nav-link ${activeTab === id ? 'active' : ''}`}
        >
          <Icon size={16} />
          <span>{label}</span>
        </button>
      ))}
    </nav>
  );

  const HomePage = () => (
    <div>
      <section className="hero">
        <div className="hero-overlay"></div>
        <img 
          src={backgroundImages[currentImageIndex]}
          alt="Land Cruiser Adventure" 
          className="hero-image"
          key={currentImageIndex}
        />
      <div className="hero-content">
        <div className="hero-text">
          <h1>Adventure Awaits</h1>
          <p>Birmingham's Premier Land Cruiser Community</p>
          <p>Where Heritage Meets Adventure</p>
        </div>
      </div>
      </section>

      <section className="section">
        <div 
          className="card card-with-bg"
          style={{
            backgroundImage: `url('/images/land-cruiser-3.jpg'), linear-gradient(135deg, rgba(0, 0, 0, 0.85) 0%, rgba(23, 23, 23, 0.8) 100%)`,
            backgroundBlendMode: 'overlay'
          }}
        >
          <div className="about-content">
            <h2>About Our Chapter</h2>
            
            <div className="about-text">
              <p className="intro-text">
                Welcome to Dixieland Cruisers! We're Birmingham's hub for all things Toyota 4WD.
              </p>
              
              <p className="description-text">
                From rugged FJ40s to built-up 4Runners, if it's Toyota and made to get dirty, 
                you've got a place here.
              </p>
              
              <p className="mission-text">
                Since 2008, we've hit the trails, camped out, turned wrenches, and made lifelong friends along the way.
              </p>
            </div>

            <div className="feature-list">
              <div className="feature-item">
                <Users size={24} />
                <span>50+ Active Members</span>
              </div>
              <div className="feature-item">
                <MapPin size={24} />
                <span>Monthly Trail Runs</span>
              </div>
              <div className="feature-item">
                <Wrench size={24} />
                <span>Wrench Days & Workshops</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <h2 style={{color: 'white', fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center'}}>
          Upcoming Adventures
        </h2>
        <div className="grid grid-2">
          {events.map((event) => (
            <div 
              key={event.id} 
              className="event-card"
              style={{
                backgroundImage: `url('${event.image}'), linear-gradient(135deg, rgba(15, 23, 42, 0.9) 0%, rgba(30, 41, 59, 0.8) 100%)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundBlendMode: 'overlay',
                minHeight: '300px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end'
              }}
            >
              <div style={{background: 'rgba(0, 0, 0, 0.7)', padding: '1.5rem', borderRadius: '0.75rem', backdropFilter: 'blur(10px)'}}>
                <h3 style={{color: 'white', marginBottom: '0.75rem'}}>{event.title}</h3>
                <div className="event-meta" style={{marginBottom: '0.5rem'}}>
                  <Calendar size={16} />
                  <span>{formatDate(event.date)} at {event.time}</span>
                </div>
                <div className="event-location" style={{marginBottom: '0.75rem'}}>
                  <MapPin size={16} />
                  <span>{event.location}</span>
                </div>
                <p style={{color: '#e2e8f0', marginBottom: '1rem'}}>{event.description}</p>
                <div className="event-footer">
                  <span className="event-attendees" style={{color: '#cbd5e1'}}>{event.attendees} attending</span>
                  <button className="btn btn-primary">Join Adventure</button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div style={{textAlign: 'center', marginTop: '2rem'}}>
          <button 
            onClick={() => handleTabChange('events')}
            className="btn btn-secondary"
            style={{fontSize: '1rem', padding: '1rem 2rem'}}
          >
            View All Adventures
          </button>
        </div>
      </section>
    </div>
  );

  const renderActiveTab = () => {
    console.log('Rendering tab:', activeTab); // Debug log
    switch(activeTab) {
      case 'home': return <HomePage />;
      case 'gallery': return <Gallery />;
      case 'privacy': return <PrivacyPolicyPage />; 
      default: return (
        <div style={{
          textAlign: 'center', 
          padding: '4rem 2rem',
          background: 'rgba(30, 41, 59, 0.5)',
          borderRadius: '1.5rem',
          backdropFilter: 'blur(20px)'
        }}>
          <h2 style={{color: 'white', marginBottom: '1rem'}}>Coming Soon</h2>
          <p style={{color: '#94a3b8'}}>This page is under construction. Check back soon for more Land Cruiser content!</p>
        </div>
      );
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>      
      <header className="header">
        <div className="container">
          <div className="header-content">
          <div className="logo" onClick={() => handleTabChange('home')} style={{ cursor: 'pointer' }}>
            <div className="logo-container">
              <img 
                src="/images/logo.png" 
                alt="Dixieland Cruisers Logo" 
                className="logo-image"
              />
            </div>
            <div className="logo-text">
              <p className="logo-location">Birmingham, AL</p>
              <p className="logo-est">EST. 2008</p>
            </div>
          </div>

            <div style={{display: 'flex', alignItems: 'center'}}>
              <NavMenu />
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="mobile-menu-button"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="main">
        {activeTab === 'gallery' ? (
          renderActiveTab()
        ) : (
          <div className="container">
            {renderActiveTab()}
          </div>
        )}
      </main>

      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <p>&copy; 2025 Dixieland Cruisers. All rights reserved.</p>
            <p style={{ marginTop: '0.5rem' }}>
              <button 
                onClick={() => handleTabChange('privacy')}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: '#60a5fa', 
                  textDecoration: 'underline', 
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                Privacy Policy
              </button>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DixielandCruisersApp;